import logger from "../../utils/logger";
import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  Spinner,
  InputGroup,
  CardBody,
} from "reactstrap";

// redux
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { authCheck, loginUser } from "../../slices/apps/authSlice";

// router
import { Link, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react input mask
import { IMaskInput } from "react-imask";

// görsel importlar
import Logo from "../../assets/images/logo/metiner_avantaj_logo.png";

const Login = () => {
  document.title = "Giriş Yap | Metiner Avantaj+";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // states
  const [passwordShow, setPasswordShow] = useState(false);

  const { loading, authCheckLoading } = useSelector(
    (state) => ({
      loading: state.auth.loading,
      authCheckLoading: state.auth.authCheckLoading,
    }),
    shallowEqual
  );

  useEffect(() => {
    const storedAuthUserString = localStorage.getItem("authUser");
    if (!storedAuthUserString) return;

    const authCheckFunc = async () => {
      try {
        await dispatch(authCheck()).unwrap();
        navigate("/dashboard");
      } catch (error) {
        logger.error("Authentication check failed:", error);
      }
    };

    authCheckFunc();
  }, [dispatch, navigate]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      phoneNumber: "",
      password: "",
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .max(20, "Telefon numarası en fazla 20 karakter olabilir.")
        .required("Lütfen telefon numarası giriniz."),
      password: Yup.string()
        .required("Lütfen şifrenizi giriniz")
        .min(6, "Şifreniz en az 6 karakter olmalıdır."),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(loginUser(values)).unwrap();
        navigate("/dashboard");
      } catch (error) {
        logger.error("Login failed:", error);
      }
    },
  });

  if (authCheckLoading) {
    return (
      <div id="preloader">
        <div id="status">
          <img
            src={Logo}
            className="mb-2"
            alt="Metiner Avantaj+"
            style={{ objectFit: "contain", width: "10rem" }}
          />
          <div className="spinner-border text-primary avatar-xs" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="auth-page-wrapper d-flex justify-content-center align-items-center min-vh-100">
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="overflow-hidden">
                  <div className="text-center pt-5">
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={Logo} alt="Metiner Avantaj+" height="100" />
                    </Link>
                  </div>
                  <CardBody className="p-lg-5 p-4">
                    <Form onSubmit={validation.handleSubmit} autoComplete="off">
                      <Row className="gy-3">
                        <Col lg={12}>
                          <Label>Telefon Numarası</Label>
                          <InputGroup>
                            <span
                              className="input-group-text"
                              style={{
                                width: "44px",
                                justifyContent: "center",
                              }}
                            >
                              0
                            </span>
                            <IMaskInput
                              inputMode="numeric"
                              mask="000 000 00 00"
                              unmask={true}
                              name="phoneNumber"
                              placeholder="5XX XXX XX XX"
                              value={validation.values.phoneNumber}
                              className={`form-control ${
                                validation.touched.phoneNumber &&
                                validation.errors.phoneNumber
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ zIndex: 0 }}
                              onAccept={(value) =>
                                validation.setFieldValue("phoneNumber", value)
                              }
                            />
                          </InputGroup>
                          {validation.touched.phoneNumber &&
                            validation.errors.phoneNumber && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.phoneNumber}
                              </div>
                            )}
                        </Col>

                        <Col lg={12}>
                          <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">
                              Şifrenizi mi unuttunuz?
                            </Link>
                          </div>
                          <Label>Şifre</Label>
                          <InputGroup>
                            <span
                              className="input-group-text"
                              style={{ width: "44px" }}
                            >
                              <i
                                onClick={() => setPasswordShow(!passwordShow)}
                                style={{ cursor: "pointer", color: "gray" }}
                                className={
                                  passwordShow
                                    ? "ri-eye-off-fill align-middle"
                                    : "ri-eye-fill align-middle"
                                }
                              ></i>
                            </span>
                            <Input
                              type={passwordShow ? "text" : "password"}
                              name="password"
                              className="form-control"
                              placeholder="******"
                              value={validation.values.password}
                              onChange={validation.handleChange}
                              invalid={
                                !!(
                                  validation.touched.password &&
                                  validation.errors.password
                                )
                              }
                            />
                          </InputGroup>
                          {validation.touched.password &&
                            validation.errors.password && (
                              <div className="d-block invalid-feedback">
                                {validation.errors.password}
                              </div>
                            )}
                        </Col>

                        <Col lg={12} className="mt-4">
                          <Button
                            type="submit"
                            color="primary"
                            className="w-100"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2" />{" "}
                                Yükleniyor...
                              </>
                            ) : (
                              "Giriş Yap"
                            )}
                          </Button>
                        </Col>
                      </Row>

                      <div className="mt-4 text-center">
                        <p className="mb-0">
                          Henüz hesabınız yok mu?{" "}
                          <Link
                            to="/register"
                            className="fw-semibold text-primary text-decoration-underline"
                          >
                            İş Ortağımız Ol
                          </Link>
                        </p>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <footer className="footer h-auto pb-2">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted">
                  <p className="mb-2" style={{ letterSpacing: "0.8px" }}>
                    Metiner Avantaj+ &copy; {new Date().getFullYear()} | 0 352 3
                    555 444 | metiner@boschservice.com.tr
                    <br />
                    <i className="mdi mdi-heart text-danger"></i> by{" "}
                    <a
                      href="https://kodexis.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "#007bff" }}
                    >
                      Kodexis
                    </a>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default Login;
